import React from "react";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericTooltip from "@ui-components/Tooltip/GenericTooltip";
import type { UseChangeRequestFormReturn } from "../../hooks/useChangeRequestForm";

interface RequestInfoTabProps {
  isKontrolEdge: boolean;
  form: UseChangeRequestFormReturn;
  cloud: string;
  region: string;
  affectedServices: string;
  getPriorityOptions: () => string[];
  isAdditionalInfoValid: () => boolean;
  onBack: () => void;
  onNext: () => void;
}

export const RequestInfoTab: React.FC<RequestInfoTabProps> = ({
  isKontrolEdge,
  form,
  cloud,
  region,
  getPriorityOptions,
  isAdditionalInfoValid,
  onBack,
  onNext,
}) => {
  return (
    <div className="space-y-4 text-sm text-gray-700 flex flex-col h-full">
      <div className="flex gap-4">
        <div className="w-1/2">
          <label className="block mb-1 font-medium text-semantic-text-secondary">
            Priority <span className="text-red-500">*</span>
          </label>
          <GenericDropdown
            items={getPriorityOptions()}
            value={form.formState.priority}
            onSelect={form.setPriority}
            defaultLabel="Select Priority"
            minWidthClass=""
            maxWidthClass=""
            widthClass="w-full"
            fixedPosition={true}
          />
        </div>
      </div>

      {!isKontrolEdge && (
        <div className="flex gap-4">
          <div className="w-1/2">
            <label className="block mb-1 font-medium">
              Cloud <span className="text-red-500">*</span>
            </label>
            <GenericInput value={cloud} disabled />
          </div>
          <div className="w-1/2">
            <label className="block mb-1 font-medium">
              Region <span className="text-red-500">*</span>
            </label>
            <GenericInput value={region} disabled />
          </div>
        </div>
      )}

      <div>
        <label className="block mb-1 font-medium flex items-center gap-1 text-semantic-text-secondary">
          Title <span className="text-red-500">*</span>
          <GenericTooltip
            text="At least 5 characters (ignoring spaces & punctuation)."
            position="top"
          />
        </label>
        <GenericInput
          placeholder="Short summary of the incident"
          value={form.formState.title}
          onChange={form.setTitle}
        />
      </div>

      <div>
        <label className="block mb-1 font-medium flex items-center gap-1 text-semantic-text-secondary">
          Description <span className="text-red-500">*</span>
          <GenericTooltip
            text="At least 10 characters (ignoring spaces & punctuation)."
            position="top"
          />
        </label>
        <textarea
          className="w-full h-24 px-3 py-2 border border-semantic-border-secondary bg-semantic-bg-primary rounded-sm text-sm text-semantic-text-secondary"
          placeholder="Describe what happened"
          value={form.formState.description}
          onChange={(e) => form.setDescription(e.target.value)}
        />
      </div>

      {/* Buttons fixed at bottom */}
      <div className="sticky bottom-0 bg-[var(--color-semantic-bg-primary)] pt-4 pb-2 border-t border-gray-200 -mx-4 px-4 mt-auto">
        <div className="flex justify-end gap-2">
          <GenericButton
            label="Back"
            variant="secondary"
            onClick={onBack}
          />
          <GenericButton
            label="Next"
            variant="brand"
            onClick={onNext}
            disabled={!isAdditionalInfoValid()}
          />
        </div>
      </div>
    </div>
  );
};
